<template>
  <v-container>
    <perseu-card title="Plano detalhado">
      <template #content>
        <v-form ref="form">
          <v-select
            :items="planTypes"
            item-value="value"
            item-text="label"
            :readonly="!isNewPlan()"
            :disabled="!isNewPlan()"
            :rules="[$rules.required]"
            v-model="plan.planType"
            label="Tipo de plano"
          />

          <v-switch 
            label="Publicamente visível"
            v-model="plan.publicVisible"
            v-if="hasSelectedPlanType()"
          />

          <v-switch
            label="Plano recomendado?"
            v-model="plan.isRecommended"
            v-if="hasSelectedPlanType() && isAppPlanType()"
            hint="Irá mostrar em destaque como plano recomendado na listagem de planos no APP"
          />

          <v-text-field
            v-model="plan.idPaypal"
            :rules="[$rules.required, idPaypalRule]"
            :disabled="isPlanActive()"
            v-if="hasSelectedPlanType()"
            label="Identificador do Plano"
            hint="Apenas letras, números e hífen são permitidos"
            @input="onIdPaypalInput"
          />

          <v-text-field
            label="Nome"
            v-model="plan.name"
            :rules="[$rules.required]"
            v-if="hasSelectedPlanType()"
          />

          <v-text-field
            v-model="plan.shortDescription"
            label="Descrição curta"
            v-if="hasSelectedPlanType() && !isAppPlanType()"
            hint="Texto secundário que aparecerá no card"
          />

          <v-text-field 
            label="Descrição"
            v-model="plan.description"
            v-if="hasSelectedPlanType()"
          />
          
          <v-textarea
            v-model="plan.tags"
            label="Tags"
            v-if="hasSelectedPlanType() && !isAppPlanType()"
            hint="Liste as tags do plano (uma por linha)"
            rows="4"
            @input="onTagsInput"
          />

          <v-row v-if="hasSelectedPlanType() && !isAppPlanType()">
            <v-col cols="12" md="8">
              <v-text-field
                v-model="plan.icon"
                label="Icone"
                hint="Nome do ícone Material Design (ex: 'mdi-star', 'mdi-diamond')"
              >
                <template v-slot:append>
                  <v-btn
                    icon
                    color="secondary"
                    @click="openIconsLibrary"
                    title="Abrir biblioteca de ícones"
                  >
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <gradient-color-picker
                v-model="plan.iconColor"
                label="Cor do ícone"
                hint="Clique para selecionar as cores do ícone"
              />
            </v-col>
          </v-row>

          <v-select
            :items="therapyTemplates"
            item-value="value"
            item-text="label"
            :rules="[$rules.required]"
            v-model="plan.therapyTemplateId"
            label="Modelo de terapia"
            v-if="hasSelectedPlanType() && !isAppPlanType()"
          />

          <v-select
            :items="types"
            item-value="value"
            item-text="label"
            label="Tipo do produto"
            :rules="[$rules.required]"
            v-model="plan.productType"
            v-if="hasSelectedPlanType() && isAppPlanType()"
          />

          <perseu-input-image-aws
            v-model="plan.image"
            class="my-5"
            label="Imagem"
            v-if="hasSelectedPlanType() && isAppPlanType()"
          />

          <v-text-field
            readonly
            disabled
            type="text"
            v-model="plan.price"
            v-if="!isNewPlan() && hasSelectedPlanType()"
            label="Valor mensal"
          />
          <v-text-field
            type="text"
            v-money="money"
            :rules="[$rules.required]"
            v-if="isNewPlan() && hasSelectedPlanType()"
            label="Valor mensal"
            v-model="plan.price"
          />
          <v-text-field
            type="number"
            v-model.number="plan.trialDays"
            v-if="!isNewPlan() && hasSelectedPlanType()"
            label="Dias de teste"
            :rules="trialDaysRules"
            min="0"
            max="9999"
            hint="Entre 0 e 9999 dias"
          />
          <v-text-field
            v-model="plan.status"
            label="Status"
            v-if="hasSelectedPlanType()"
            readonly
            disabled
          />
          <v-text-field
            :value="plan.expirationDate | formatDate"
            label="Data de expiração"
            v-if="hasSelectedPlanType() && isAppPlanType()"
            readonly
            disabled
          />
        </v-form>
      </template>
      <template #actions>
        <v-btn outlined @click="$router.go(-1)"> Voltar</v-btn>
        <v-btn
          @click="redirectToPlanUsersEmail"
          color="primary"
          :disabled="!plan.idPaypal"
        >
          Gerenciar e-mails
        </v-btn>
        <v-btn
          color="secondary"
          @click="createOrUpdate"
          :disabled="!hasSelectedPlanType()"
        >
          Salvar
        </v-btn>
      </template>
    </perseu-card>
    <benefits-section
      class="my-3"
      :benefits="plan.benefits"
      :id-plan="plan.idPaypal"
      @add-benefit="addBenefit"
      v-if="plan.price"
      @update-benefit="updateBenefit"
      @remove-benefit="removeBenefit"
    />
  </v-container>
</template>

<script>
import {
  getProductsType,
  getPlansType,
  getOnePlan,
  putPlan,
  postPlan,
} from "@/services/paypal-service";
import { findForCombo } from "@/services/therapy-templates-service";
import {Money} from 'v-money'
import { pagesNames } from "@/router/pages-names";

export default {
  name: "Plan",
  components: {
    PerseuInputImageAws: () =>
      import("@/components/shared/PerseuInputImageAWS"),
    BenefitsSection: () => import("@/components/Paypal/BenefitsSection"),
    GradientColorPicker: () => import("@/components/Paypal/GradientColorPicker"),
  },
  data: () => ({
    plan: {
      benefits: [],
      plans: [],
    },
    types: [],
    planTypes: [],
    therapyTemplates: [],
    money: {
      masked: true,
      precision: 2,
      decimal: ',',
      thousands: '.',
      prefix: 'R$ ',
      allowBlank: true
    }
  }),
  
  computed: {
    trialDaysRules() {
      return [
        v => v === null || v === undefined || v === '' || Number.isInteger(Number(v)) || 'Deve ser um número inteiro',
        v => v === null || v === undefined || v === '' || Number(v) >= 0 || 'Deve ser maior ou igual a 0',
        v => v === null || v === undefined || v === '' || Number(v) <= 9999 || 'Deve ser menor ou igual a 9999'
      ];
    },
    idPaypalRule() {
      return v => {
        if (!v) return true; // Permite vazio, pois o required já valida isso
        const regex = /^[a-zA-Z0-9-]+$/;
        return regex.test(v) || 'Apenas letras, números e hífen são permitidos';
      };
    }
  },

  created() {
    this.fillSelectedPlan();
    this.loadTypes();
    this.loadPlanTypes();
    this.loadTherapyTemplates();
  },

  methods: {
    async fillSelectedPlan() {
      if (this.$route.query.idPaypal) {
        this.$store.dispatch("loading/openDialog");
        const { data } = await getOnePlan(this.$route.query.idPaypal);
        this.plan = data;
        this.$store.dispatch("loading/closeDialog");
      }
    },
    async loadTypes(planId) {
      this.$store.dispatch("loading/openDialog");
      const { data } = await getProductsType(planId);
      this.types = data;
      this.$store.dispatch("loading/closeDialog");
    },

    async loadPlanTypes() {
      this.$store.dispatch("loading/openDialog");
      const { data } = await getPlansType();
      this.planTypes = data;
      this.$store.dispatch("loading/closeDialog");
    },
    async loadTherapyTemplates() {
      this.$store.dispatch("loading/openDialog");
      const { data } = await findForCombo();
      this.therapyTemplates = data;
      this.$store.dispatch("loading/closeDialog");
    },
    async createOrUpdate() {
      try {
        this.$store.dispatch("loading/openDialog");
        if (!this.$refs.form.validate()) return;
        let response;
        if (!this.isAppPlanType()) {
          this.plan.productType = "ADVANCED";
        }
        console.log("plan", this.plan);
        if (!this.isNewPlan()) {
          response = await this.update();
        } else {
          response = await this.create();
        }
        this.plan = { ...this.plan, ...response };
        this.$toasted.global.defaultSuccess();
      } catch (error) {
        this.$errorHandler(error);
      } finally {
        this.$store.dispatch("loading/closeDialog");
      }
    },
    async create() {
      const { data } = await postPlan(this.plan);
      return data;
    },
    async update() {
      const { data } = await putPlan(this.plan);
      return data;
    },
    addBenefit({ benefit }) {
      this.plan.benefits.push(benefit);
    },
    findIndexBenefit(id) {
      return this.plan.benefits.findIndex((benefit) => benefit.id === id);
    },
    removeBenefit({ idBenefit }) {
      const foundIndex = this.findIndexBenefit(idBenefit);
      this.plan.benefits.splice(foundIndex, 1);
    },
    updateBenefit({ benefit }) {
      const foundIndex = this.findIndexBenefit(benefit.id);
      this.plan.benefits.splice(foundIndex, 1, benefit);
    },
    redirectToPlanUsersEmail() {
      this.$router.push({
        name: pagesNames.PAYPAL_PLAN_USERS_EMAIL,
        query: { planId: this.plan.idPaypal, planName: this.plan.name },
      });
    },
    hasSelectedPlanType() {
      return !!this.plan.planType;
    },
    isPlanActive() {
      return this.plan?.status === "ACTIVE";
    },
    isAppPlanType() {
      return (
        this.plan.planType === "PAYPAL_SUBSCTIPTION" ||
        this.plan.planType === "STRIPE_SUBSCTIPTION"
      );
    },
    isNewPlan() {
      return !this.$route.query.idPaypal;
    },
    openIconsLibrary() {
      window.open('https://pictogrammers.com/library/mdi/', '_blank');
    },
    onIdPaypalInput(value) {
      this.plan.idPaypal = value.toUpperCase();
    },
    onTagsInput(value) {
      this.plan.tags = value.trim();
    },
  },
};
</script>

<style scoped></style>
